import React, { memo } from 'react';

import Fab from '@mui/material/Fab';
import { Stack, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { useBack } from 'src/hooks/useBack';

import useResponsive from '../../hooks/useResponsive';

// ----------------------------------------------------------------------

type FormActionButtonsProps = {
  gridView: string;
  setGridView: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: (generateDocument: boolean) => void;
  disabled: boolean;
  saveDisabled: boolean;
};

const FormActionButtons: React.FC<FormActionButtonsProps> = ({
  gridView,
  setGridView,
  handleSubmit,
  disabled,
  saveDisabled,
}) => {
  const goBack = useBack('/dashboard');

  const isGridColumnView = gridView === 'columns';

  const isSmallDisplay = useResponsive('down', 'lg');

  const handleGridViewChange = (newGridView: string) => {
    if (newGridView) {
      setGridView(newGridView);
      localStorage.setItem('formGridView', newGridView);
    }
  };

  return (
    <Stack sx={{ position: 'fixed', right: 0, bottom: 0, mr: 2, mb: 2 }} spacing={1}>
      <Tooltip
        title="Back"
        placement="left"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -6],
                },
              },
            ],
          },
        }}
      >
        <Fab color="info" size="medium" onClick={goBack} aria-label="back">
          <UndoIcon />
        </Fab>
      </Tooltip>

      <Tooltip
        title="Save"
        placement="left"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -6],
                },
              },
            ],
          },
        }}
      >
        <Fab
          color="primary"
          size="medium"
          onClick={() => handleSubmit(false)}
          aria-label="edit"
          disabled={saveDisabled}
        >
          <SaveIcon />
        </Fab>
      </Tooltip>

      <Tooltip
        title="Generate"
        placement="left"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -6],
                },
              },
            ],
          },
        }}
      >
        <Fab
          color="error"
          size="medium"
          aria-label="edit"
          onClick={() => handleSubmit(true)}
          disabled={disabled}
        >
          <PictureAsPdfIcon />
        </Fab>
      </Tooltip>

      {!isSmallDisplay && (
        <Tooltip
          title="Layout"
          placement="left"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -6],
                  },
                },
              ],
            },
          }}
        >
          <Fab
            color="secondary"
            size="medium"
            onClick={() => handleGridViewChange(isGridColumnView ? 'rows' : 'columns')}
            aria-label="edit"
          >
            {isGridColumnView ? <TableRowsIcon /> : <ViewSidebarIcon />}
          </Fab>
        </Tooltip>
      )}
    </Stack>
  );
};

export default memo(FormActionButtons);
